import { brand } from "../../config/theme";
// React
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Contextos
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { isMenuItemEnabled } from "../../config/appsConfig";
import { useUsersStore } from "../../store/admin/usersStore";
import { useDevicesStore } from "../../store/admin/devicesStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

// Componentes
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons";

// Utilidades
import { useTheme } from "../../context/ThemeContext";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";

export default function CobranzaLayout() {
  // Estado globales
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const hasRedirected = useRef(false);

  // Company selector state
  const companyId = useCompaniesStore((state) => state.companyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);

  // Verificar si es reseller por ROL (no por compañía)
  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // Users store for count
  const usersCount = useUsersStore((state) => state.meta?.totalItems ?? 0);
  const usersLoading = useUsersStore((state) => state.loading);
  const fetchUsers = useUsersStore((state) => state.fetchUsers);

  // Devices store for count
  const devicesCount = useDevicesStore((state) => state.meta?.totalItems ?? 0);
  const devicesLoading = useDevicesStore((state) => state.loading);
  const fetchDevices = useDevicesStore((state) => state.fetchDevices);

  // Set active app for dynamic header title/subtitle and icon
  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "cobranza",
      t("apps.cobranza.name"),
      t("apps.cobranza.description"),
      faClipboardList,
      "bg-emerald-500"
    );
  }, [setActiveApp, t]);

  // Sincronizar companyId desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    } else if (!storedCompanyId && storedCompanyUser && !isReseller) {
      setCompanyId(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar lista de compañías cuando es reseller
  useEffect(() => {
    if (isReseller && allCompanies.length === 0) {
      fetchAllCompanies();
    }
  }, [isReseller, allCompanies.length, fetchAllCompanies]);

  // Cargar usuarios al montar o cuando cambia companyId
  useEffect(() => {
    fetchUsers({ limit: 8, page: 1 });
  }, [companyId, fetchUsers]);

  // Cargar dispositivos al montar o cuando cambia companyId
  useEffect(() => {
    fetchDevices({ limit: 6, page: 1 });
  }, [companyId, fetchDevices]);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Estado local
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Redirect to resumen by default
  useEffect(() => {
    if (location.pathname === "/cobranza" || location.pathname === "/cobranza/") {
      navigate("/cobranza/resumen", { replace: true });
    }
  }, [location.pathname, navigate]);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "cobranza") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  const menuItems = [
    {
      id: "resumen",
      label: t("sidebarMenu.summary"),
      icon: "ri-dashboard-line",
      dividerAfter: true,
    },
    {
      id: "companies",
      label: t("sidebarMenu.companies"),
      icon: "ri-building-2-line",
      count: 0,
      loading: false,
    },
    {
      id: "usuarios",
      label: t("sidebarMenu.users"),
      icon: "ri-user-line",
      count: usersCount,
      loading: usersLoading,
    },
    {
      id: "dispositivos",
      label: t("sidebarMenu.devices"),
      icon: "ri-smartphone-line",
      count: devicesCount,
      loading: devicesLoading,
      dividerAfter: true,
    },
    {
      id: "pagos",
      label: "Pagos",
      icon: "ri-money-dollar-circle-line",
    },
    {
      id: "adeudos",
      label: "Adeudos",
      icon: "ri-file-warning-line",
    },
    {
      id: "historial",
      label: t("sidebarMenu.paymentHistory"),
      icon: "ri-history-line",
    },
  ];

  // Filter menu items based on app config
  const filteredMenuItems = menuItems.filter((item) =>
    isMenuItemEnabled("internos", "cobranza", item.id),
  );

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <div
      className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
    >
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}
      <Header 
        onOpenAppsSidebar={openAppsSidebar} 
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={filteredMenuItems.map((item) => ({
            ...item,
            path: `/cobranza/${item.id}`,
          }))}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname.endsWith(itemId)}
          companyIndicator={{
            companyId,
            companyName:
              allCompanies.find((c) => c.id === companyId)?.name ||
              allCompanies.find((c) => c.id === companyId)?.business_name ||
              companyId,
            isReseller,
            isDarkMode,
          }}
        />
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="cobranza"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
